import { createFileRoute } from "@tanstack/react-router";
import { MinimalFooter } from "@/components/feo/MinimalFooter";
import { useMemo, useState } from "react";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Trophy,
  Bone,
  Brain,
  Activity,
  Medal,
} from "lucide-react";
import logoFeo from "@/assets/feo/logo-feo.png";
import { Button } from "@/components/ui/button";

import posOrtopedia from "@/assets/pos/thumb-ortopedia.webp";
import posDor from "@/assets/pos/thumb-dor.webp";
import posPrescricao from "@/assets/pos/thumb-prescricao.webp";
import posDorBanner from "@/assets/pos-dor-banner.jpg.asset.json";
import posOrtoBanner from "@/assets/pos-orto-banner.jpg.asset.json";

import { QuizzQuestionCard } from "@/components/quizz/QuizzQuestionCard";
import { QuizzContactStep } from "@/components/quizz/QuizzContactStep";
import { useDorQuizContent } from "@/lib/quizz/use-quizz-content";
import type { DorContent } from "@/lib/quizz/content";
import { RichText } from "@/lib/quizz/rich-text";

import {
  estimateQualifierTotal,
  nextQualifierQuestion,
  type QualifierAnswer,
} from "@/lib/leads/qualifier-questions";

export const Route = createFileRoute("/quizz-dor-pos-todas")({
  head: () => ({
    meta: [
      { title: "Quizz Dor" },
      {
        name: "description",
        content:
          "Responda 5 perguntas sobre dor e descubra qual pós-graduação combina com a sua próxima fase como fisioterapeuta.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Quizz Dor" },
      {
        property: "og:description",
        content: "5 perguntas rápidas sobre dor. Descubra a pós que mais combina com você.",
      },
    ],
  }),
  component: QuizzDorPage,
});

// ---------- Pós ----------

type PosSlug = "orto" | "dor" | "prescricao";

type Pos = {
  slug: PosSlug;
  name: string;
  /** Título alternativo, usado só no mobile quando existir. */
  nameMobile?: string;
  short: string;
  image: string;
  /** Versão horizontal (16:9), usada só no mobile quando existir. */
  imageMobile?: string;
  url: string;
  bullets: string[];
  Icon: typeof Bone;
};

const POS: Record<PosSlug, Pos> = {
  orto: {
    slug: "orto",
    name: "Pós de Fisioterapia em Ortopedia e Traumatologia",
    nameMobile: "Pós de Fisioterapia em Ortopedia e Trauma",
    short: "Pós de Ortopedia e Trauma",
    image: posOrtopedia,
    imageMobile: posOrtoBanner.url,
    url: "https://pos-orto.fisioemortopedia.com.br/",
    Icon: Bone,
    bullets: [
      "Prática baseada em evidências para orientar suas decisões",
      "Dor e modelo biopsicossocial aplicados à clínica",
      "Avaliação musculoesquelética, na prática",
      "Princípios das intervenções terapêuticas",
      "Reabilitação da coluna, MMSS, MMII, ATM e Trauma",
    ],
  },

  dor: {
    slug: "dor",
    name: "Especialização em Dor para Fisioterapeutas",
    short: "Especialização em Dor",
    image: posDor,
    imageMobile: posDorBanner.url,
    url: "https://www.pos-dor.fisioemortopedia.com.br/",
    Icon: Brain,
    bullets: [
      "Ciência da dor: epidemiologia, neurofisiologia e fenótipos",
      "Avaliação e raciocínio clínico do paciente, na prática",
      "Intervenções fisioterapêuticas aplicadas ao manejo da dor",
      "Dor aguda, neuropática e crônicas primárias e secundárias",
      "Abordagem biopsicossocial e interdisciplinar da dor",
    ],
  },
  prescricao: {
    slug: "prescricao",
    name: "Pós-graduação em Prescrição de Exercícios Terapêuticos",
    short: "Pós em Prescrição de Exercícios",
    image: posPrescricao,
    url: "https://pos-prescricao.fisioemortopedia.com.br/",
    Icon: Activity,
    bullets: [
      "Carga, dose e progressão de exercício",
      "Reabilitação por exercício terapêutico",
      "Prescrição individualizada e baseada em evidências",
    ],
  },
};

// ---------- Página ----------
//
// Fluxo:
//   intro → bloco 1 (qualificação) + bloco 2 (perguntas do quiz), sem divisão
//   visível → bloco final (nome/e-mail/telefone) → resultado.
//
// A captura/qualificação (RD Station + CRM) usa APENAS o bloco 1.
// O bloco 2 gera somente a nota (ouro/prata/bronze) e o gabarito.

type Phase = "intro" | "questions" | "lead" | "result";

function QuizzDorPage() {
  const content = useDorQuizContent();
  const QUESTIONS = content.questions;

  const [phase, setPhase] = useState<Phase>("intro");
  const [qualAnswers, setQualAnswers] = useState<QualifierAnswer[]>([]);
  const [quizAnswers, setQuizAnswers] = useState<Array<number | undefined>>([]);
  const [quizIndex, setQuizIndex] = useState(0);

  const qualQuestion = nextQualifierQuestion(qualAnswers);
  const inQualBlock = qualQuestion !== null;

  const totalSteps = estimateQualifierTotal(qualAnswers) + QUESTIONS.length;
  const stepIndex = qualAnswers.length + (inQualBlock ? 0 : quizIndex);

  const acertos = useMemo(
    () =>
      quizAnswers.reduce<number>(
        (acc, a, i) => acc + (a !== undefined && a === QUESTIONS[i]?.correct ? 1 : 0),
        0,
      ),
    [quizAnswers, QUESTIONS],
  );

  // 0-1 acertos (bronze) → Pós Orto principal; 2+ (prata/ouro) → Pós Dor principal
  const recommendation = useMemo(() => {
    if (acertos >= 2) {
      return { area: "dor" as const, winner: POS.dor, suggestions: [POS.prescricao, POS.orto] };
    }
    return { area: "orto" as const, winner: POS.orto, suggestions: [POS.dor, POS.prescricao] };
  }, [acertos]);

  function start() {
    setQualAnswers([]);
    setQuizAnswers(Array(QUESTIONS.length).fill(undefined));
    setQuizIndex(0);
    setPhase("questions");
  }

  function pickQualOption(optIdx: number) {
    if (!qualQuestion) return;
    const opt = qualQuestion.options[optIdx];
    const next: QualifierAnswer[] = [
      ...qualAnswers,
      {
        pergunta: qualQuestion.pergunta,
        resposta: opt.label,
        pontos: opt.pontos,
        dealBreaker: !!opt.dealBreaker,
      },
    ];
    setQualAnswers(next);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function pickQuizOption(optIdx: number) {
    const next = [...quizAnswers];
    next[quizIndex] = optIdx;
    setQuizAnswers(next);
  }

  function nextQuizQuestion() {
    if (quizAnswers[quizIndex] === undefined) return;
    if (quizIndex < QUESTIONS.length - 1) {
      setQuizIndex((c) => c + 1);
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setPhase("lead");
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  function goBack() {
    if (!inQualBlock && quizIndex > 0) {
      setQuizIndex((c) => c - 1);
      return;
    }
    if (qualAnswers.length > 0) {
      setQualAnswers(qualAnswers.slice(0, -1));
      setQuizIndex(0);
      return;
    }
    setPhase("intro");
  }

  return (
    <main className="flex min-h-screen flex-col bg-background">
      <div className="flex-grow">
        {phase === "intro" && <IntroPhase onStart={start} total={QUESTIONS.length} />}

        {phase === "questions" && inQualBlock && qualQuestion && (
          <QuizzQuestionCard
            title={qualQuestion.pergunta}
            options={qualQuestion.options.map((o) => o.label)}
            onPick={pickQualOption}
            onBack={goBack}
            index={stepIndex}
            total={totalSteps}
            instant
          />
        )}

        {phase === "questions" && !inQualBlock && QUESTIONS[quizIndex] && (
          <QuizzQuestionCard
            title={QUESTIONS[quizIndex].title}
            options={QUESTIONS[quizIndex].options}
            selected={quizAnswers[quizIndex]}
            onPick={pickQuizOption}
            onNext={nextQuizQuestion}
            onBack={goBack}
            index={stepIndex}
            total={totalSteps}
            nextLabel={quizIndex === QUESTIONS.length - 1 ? "Ver meu resultado" : "Próxima"}
            showLetters
          />
        )}

        {phase === "lead" && (
          <LeadPhase
            recommendation={recommendation}
            acertos={acertos}
            total={QUESTIONS.length}
            qualAnswers={qualAnswers}
            onSuccess={() => {
              void import("@/lib/black2x1/entregas-redirect").then((m) => m.goToBlackEntregas("quizz-dor"));
            }}
            onBack={() => {
              setPhase("questions");
              setQuizIndex(QUESTIONS.length - 1);
            }}
          />
        )}

        {phase === "result" && (
          <ResultPhase
            winner={recommendation.winner}
            suggestions={recommendation.suggestions}
            acertos={acertos}
            total={QUESTIONS.length}
            answers={quizAnswers}
            questions={QUESTIONS}
            tiers={content.tiers}
            onRestart={start}
          />
        )}
      </div>

      <MinimalFooter />
    </main>
  );
}

// ---------- Sub-componentes ----------

function IntroPhase({ onStart, total }: { onStart: () => void; total: number }) {
  return (
    <section className="relative min-h-screen overflow-hidden bg-hero">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="container relative mx-auto max-w-3xl px-4 py-16 text-center sm:py-24">
        <img decoding="async"
          src={logoFeo}
          alt="Fisio em Ortopedia"
          className="mx-auto mb-12 h-16 w-auto"
        />
        <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold">
          QUIZZ EXCLUSIVO · 2 MINUTOS
        </div>
        <h1 className="mt-6 text-balance text-3xl font-extrabold leading-tight text-foreground sm:text-5xl">
          Você realmente entende como a&nbsp;<span className="text-gradient-primary">DOR</span>&nbsp;funciona?
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-balance text-base text-muted-foreground sm:text-lg">
          Teste seu raciocínio em 5 perguntas sobre neurociência da dor, sistema nervoso e avaliação clínica. Ao final, descubra em que nível está o seu conhecimento sobre dor.
        </p>
        <Button
          size="lg"
          onClick={onStart}
          className="mt-8 rounded-full bg-success px-8 text-primary-foreground hover:bg-success/90"
        >
          Começar o quizz <ArrowRight className="h-4 w-4" />
        </Button>
        <p className="mt-4 text-xs text-muted-foreground">Leva menos de 2 minutos · 100% gratuito</p>
      </div>
    </section>
  );
}

function LeadPhase({
  recommendation,
  acertos,
  total,
  qualAnswers,
  onSuccess,
  onBack,
}: {
  recommendation: { area: "orto" | "dor"; winner: Pos; suggestions: Pos[] };
  acertos: number;
  total: number;
  qualAnswers: QualifierAnswer[];
  onSuccess: () => void;
  onBack: () => void;
}) {
  const target = {
    slug: recommendation.area,
    posNome: recommendation.winner.name,
    posUrl: recommendation.winner.url,
    conversionIdentifier: "quizz-dor",
  };
  return (
    <section className="min-h-screen bg-deep py-12 sm:py-16">
      <div className="container mx-auto max-w-2xl px-4">
        <div className="rounded-2xl border border-gold/30 bg-card p-6 shadow-card sm:p-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold">
              <Trophy className="h-3.5 w-3.5" /> Quizz finalizado · {total} perguntas respondidas
            </div>
            <h2 className="mt-5 text-balance text-2xl font-bold text-foreground sm:text-3xl">
              Falta só mais um passo!
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Preencha seus dados para liberar a sua nota, o gabarito e a pós recomendada.
            </p>
          </div>

          <div className="mt-8">
            <QuizzContactStep
              target={target}
              endpoint="/api/public/leads/quizz-dor"
              sourceLabel="quizz-dor"
              qualifierAnswers={qualAnswers}
              ctaLabel="Liberar minha nota e o gabarito"
              onSuccess={onSuccess}
            />
          </div>

          <div className="mt-6 text-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-muted-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Revisar respostas do quizz
            </Button>
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {acertos > 0 ? "Sua nota já está calculada." : ""}
          </p>
        </div>
      </div>
    </section>
  );
}

function ResultPhase({
  winner,
  suggestions,
  acertos,
  total,
  answers,
  questions,
  tiers,
  onRestart,
}: {
  winner: Pos;
  suggestions: Pos[];
  acertos: number;
  total: number;
  answers: Array<number | undefined>;
  questions: DorContent["questions"];
  tiers: DorContent["tiers"];
  onRestart: () => void;
}) {
  const Icon = winner.Icon;
  const pct = total > 0 ? (acertos / total) * 100 : 0;
  const tier =
    pct > 80
      ? {
          label: tiers.ouro.label,
          ring: "ring-[#D4AF37]/40",
          bg: "bg-[#D4AF37]/10",
          border: "border-[#D4AF37]/40",
          text: "text-[#D4AF37]",
          medalFill: "#D4AF37",
          medalStroke: "#8a6d1a",
          message: tiers.ouro.message,
        }
      : pct >= 40
      ? {
          label: tiers.prata.label,
          ring: "ring-[#B8BCC2]/40",
          bg: "bg-[#B8BCC2]/10",
          border: "border-[#B8BCC2]/40",
          text: "text-[#C7CBD1]",
          medalFill: "#C0C4CC",
          medalStroke: "#6f7480",
          message: tiers.prata.message,
        }
      : {
          label: tiers.bronze.label,
          ring: "ring-[#CD7F32]/40",
          bg: "bg-[#CD7F32]/10",
          border: "border-[#CD7F32]/40",
          text: "text-[#E08A4A]",
          medalFill: "#CD7F32",
          medalStroke: "#6b3f17",
          message: tiers.bronze.message,
        };

  return (
    <section className="min-h-screen bg-deep py-14 sm:py-20">
      <div className="container mx-auto max-w-5xl px-4">
        {/* Resultado: medalha + texto */}
        <div className="mx-auto max-w-3xl">
          <div className={`overflow-hidden rounded-3xl border ${tier.border} ${tier.bg} p-6 shadow-card sm:p-10`}>
            <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:gap-8">
              <div className={`flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-full ring-4 ${tier.ring} bg-card sm:h-28 sm:w-28`}>
                <Medal
                  className="h-14 w-14 sm:h-16 sm:w-16"
                  color={tier.medalStroke}
                  fill={tier.medalFill}
                  strokeWidth={1.5}
                />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Seu resultado
                </p>
                <h2 className={`mt-2 text-4xl font-extrabold tracking-tight sm:text-5xl ${tier.text}`}>
                  {tier.label}
                </h2>
                <p className="mt-1 text-sm font-semibold text-muted-foreground">
                  {acertos} de {total} acertos · {Math.round(pct)}%
                </p>
              </div>
            </div>
            <p className="mt-6 whitespace-pre-line text-sm leading-relaxed text-foreground sm:text-base">
              <RichText text={tier.message} />
            </p>
          </div>
        </div>

        {/* Recomendação */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold">
            Seu próximo passo
          </div>

          <h3 className="mx-auto mt-3 max-w-3xl text-balance text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
            {pct > 80
              ? "Você já domina os fundamentos. Agora, avance para os casos mais complexos."
              : pct >= 40
                ? "Você já tem uma boa base. Agora precisa fazê-la funcionar na prática clínica."
                : "Foque na base. Pare de juntar as peças enquanto o paciente está na sua frente."}
          </h3>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Pelo seu quizz, a formação mais alinhada ao seu momento é a
            <br className="hidden sm:inline" />{" "}
            {winner.name}.
          </p>

        </div>


        <div className="mt-12 overflow-hidden rounded-3xl border border-gold/30 bg-card shadow-card">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="relative aspect-[16/9] bg-muted sm:aspect-[16/10] lg:aspect-auto">
              {winner.imageMobile && (
                <img loading="lazy" decoding="async"
                  src={winner.imageMobile}
                  alt={winner.name}
                  className="h-full w-full object-cover object-center lg:hidden"
                />
              )}
              <img loading="lazy" decoding="async"
                src={winner.image}
                alt={winner.name}
                className={`h-full w-full object-cover ${winner.imageMobile ? "hidden lg:block" : ""}`}
              />
            </div>
            <div className="flex flex-col justify-center p-6 sm:p-10 lg:py-12">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground shadow-glow">
                  <Icon className="h-6 w-6" />
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  PÓS INDICADA PARA O SEU MOMENTO
                </p>
              </div>
              <h3 className="mt-6 text-2xl font-bold leading-snug text-foreground sm:text-3xl">
                {winner.nameMobile && (
                  <span className="lg:hidden">{winner.nameMobile}</span>
                )}
                <span className={winner.nameMobile ? "hidden lg:inline" : undefined}>
                  {winner.name.split(" para ").map((part, i, arr) => (
                    <span key={part}>
                      {part}
                      {i < arr.length - 1 && <br />}
                      {i < arr.length - 1 && "para "}
                    </span>
                  ))}
                </span>
              </h3>
              <ul className="mt-7 space-y-3">
                {winner.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-foreground">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-success" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-9 flex flex-col items-center gap-3 sm:flex-row sm:items-start">
                <Button
                  asChild
                  size="lg"
                  className="rounded-full bg-success px-8 text-primary-foreground hover:bg-success/90 sm:h-11 sm:w-auto sm:text-base sm:font-semibold"
                >
                  <a href={winner.url} target="_blank" rel="noopener noreferrer">
                    Conhecer a pós <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
              </div>
              <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
                Nosso time entrará em contato para te orientar sobre a pós.
              </p>
            </div>
          </div>
        </div>

        {suggestions.length > 0 && (
          <div className="mt-20">
            <p className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              OUTRAS FORMAÇÕES DA FISIO EM ORTOPEDIA:
            </p>
            <div className="mx-auto mt-5 grid max-w-3xl gap-4 sm:grid-cols-2">
              {suggestions.map((s) => (
                <a
                  key={s.slug}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-4 transition hover:border-primary/50"
                >
                  <img loading="lazy" decoding="async"
                    src={s.image}
                    alt={s.name}
                    className="h-16 w-16 flex-shrink-0 rounded-xl object-cover"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-foreground">{s.short}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{s.name}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Gabarito comentado */}
        <div className="mt-14">
          <h3 className="text-center text-2xl font-bold text-foreground sm:text-3xl">
            Gabarito
          </h3>
          <div className="mx-auto mt-8 grid max-w-3xl gap-4">
            {questions.map((q, i) => {
              const idx = answers[i];
              const acertou = idx !== undefined && idx === q.correct;
              return (
                <div key={q.id} className="rounded-2xl border border-border bg-card p-5 sm:p-6">
                  <div className="flex items-start gap-3">
                    {acertou ? (
                      <CheckCircle2 className="mt-1 h-5 w-5 flex-shrink-0 text-success" />
                    ) : (
                      <XCircle className="mt-1 h-5 w-5 flex-shrink-0 text-destructive" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-foreground">Pergunta {i + 1}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{q.title}</p>
                      {!acertou && idx !== undefined && (
                        <p className="mt-3 text-sm">
                          <span className="font-semibold text-foreground">Sua resposta: </span>
                          <span className="text-muted-foreground">
                            ({String.fromCharCode(97 + idx)}) {q.options[idx]}
                          </span>
                        </p>
                      )}
                      <p className={`text-sm ${acertou ? "mt-3" : "mt-2"}`}>
                        <span className="font-semibold text-foreground">Resposta correta: </span>
                        <span className={acertou ? "text-foreground" : "italic text-foreground"}>
                          ({String.fromCharCode(97 + q.correct)}) {q.options[q.correct]}
                        </span>
                      </p>

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
